<template>
    <div>
        <div class="main-container">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId, serialNumber: serialNumber } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="gs-page-header">
                <div>
                    <h1 class="gs-page-header__title">Settings</h1>
                    <p class="gs-page-header__sub">Configure device controls and limits</p>
                </div>
                <button class="gs-reset-btn" :disabled="isDisabled" @click="openConfirmationModal()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M3 3v5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Reset
                </button>
            </div>

            <!-- Section: Usage -->
            <div class="gs-section-label">Usage Controls</div>

            <!-- Device Daily Limit -->
            <div class="gs-card">
                <div class="gs-card__header">
                    <div class="gs-card__icon gs-card__icon--blue">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">Device Daily Limit</span>
                        <span class="gs-card__desc">Disables all apps once usage exceeds this limit</span>
                    </div>
                </div>
                <div class="gs-stepper">
                    <button class="gs-stepper__btn" @click="decreaseLimit()">
                        <ArrowLeftIcon />
                    </button>
                    <input
                        type="text"
                        class="gs-stepper__input"
                        :value="displayLimit"
                        @input="handleLimitInput($event)"
                        @blur="roundOF15"
                    />
                    <button class="gs-stepper__btn" @click="increaseLimit()">
                        <ArrowRightIcon />
                    </button>
                </div>
            </div>

            <!-- Section: Location -->
            <div class="gs-section-label">Location</div>

            <!-- Location Tracking Interval -->
            <div class="gs-card">
                <div class="gs-card__header">
                    <div class="gs-card__icon gs-card__icon--green">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">Location Update Interval</span>
                        <span class="gs-card__desc">How often the device location is tracked (minutes)</span>
                    </div>
                </div>
                <div class="gs-stepper">
                    <button class="gs-stepper__btn" @click="decreaseLocationLimit()" :disabled="!enableLocationTracking">
                        <ArrowLeftIcon />
                    </button>
                    <input
                        type="text"
                        class="gs-stepper__input"
                        :value="displayLocationLimit"
                        @input="handleLocationInput($event)"
                        @blur="roundOF15Location"
                        :disabled="!enableLocationTracking"
                    />
                    <button class="gs-stepper__btn" @click="increaseLocationLimit()" :disabled="!enableLocationTracking">
                        <ArrowRightIcon />
                    </button>
                </div>
            </div>

            <!-- Section: Device Controls -->
            <div class="gs-section-label">Device Controls</div>

            <!-- Toggle rows -->
            <div class="gs-card">
                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--purple">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M8 21h8M12 17v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Limit Wingio Child App</span>
                        <span class="gs-card__desc">Restricts app features including requests</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="limitChildApp" @change="updateLimitChildApp()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gs-divider"></div>

                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--orange">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M18 8l2 2-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Disable Adding/Deleting Accounts</span>
                        <span class="gs-card__desc">Prevents account changes on the device</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="modify_accounts_disabled" @change="updateProfile()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gs-divider"></div>

                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--orange">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12.55a11 11 0 0 1 14.08 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <path d="M1.42 9a16 16 0 0 1 21.16 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <circle cx="12" cy="20" r="1" fill="currentColor"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Turn Off Hotspot</span>
                        <span class="gs-card__desc">Prevents tethering, hotspot and USB sharing</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="tethering_config_disabled" @change="updateProfile()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gs-divider"></div>

                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--blue">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" stroke-width="1.8"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Web Monitoring</span>
                        <span class="gs-card__desc">Restricts access to certain online content</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="turnOnWebMonitoring" @change="updateTurnOnWebMonitoring()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gs-divider"></div>

                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--blue">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Notification Sound</span>
                        <span class="gs-card__desc">Play sound for each incoming notification</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="enableNotificationSound" @change="updateEnableNotificationSound()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <div class="gs-divider"></div>

                <!-- Speed & Travel Alerts -->
                <div class="gs-toggle-row">
                    <div class="gs-card__icon gs-card__icon--red">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M12 8v4l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                            <path d="M12 2v2M12 20v2M2 12h2M20 12h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-toggle-row__info">
                        <span class="gs-card__title">Speed & Travel Alerts</span>
                        <span class="gs-card__desc">Get notified when speed exceeds the threshold</span>
                    </div>
                    <label class="kid-action-switch">
                        <input type="checkbox" v-model="enableSpeedAlert" @change="updateSpeedAlert()">
                        <span class="kid-action-switch__track"></span>
                    </label>
                </div>

                <!-- Speed threshold — only shown when enabled -->
                <div class="gs-speed-threshold" v-if="enableSpeedAlert">
                    <div class="gs-speed-threshold__label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Alert when speed exceeds
                    </div>
                    <div class="gs-speed-stepper">
                        <button class="gs-stepper__btn" @click="decreaseSpeedThreshold()">
                            <ArrowLeftIcon />
                        </button>
                        <div class="gs-speed-stepper__val">
                            <span class="gs-speed-stepper__num">{{ speedThreshold }}</span>
                            <span class="gs-speed-stepper__unit">km/h</span>
                        </div>
                        <button class="gs-stepper__btn" @click="increaseSpeedThreshold()">
                            <ArrowRightIcon />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Section: Security -->
            <div class="gs-section-label">Security</div>

            <!-- Settings Access Level -->
            <div class="gs-card">
                <div class="gs-card__header" style="margin-bottom: 12px;">
                    <div class="gs-card__icon gs-card__icon--red">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">Settings Access Level</span>
                        <span class="gs-card__desc">Controls what the child can access in device settings</span>
                    </div>
                </div>
                <div class="gs-access-options">
                    <label class="gs-access-option" :class="{ 'gs-access-option--active': protectionStatus == 0 }">
                        <input type="radio" v-model="protectionStatus" :value="0" @change="updateAllowFullAccess()" />
                        <span class="gs-access-option__dot" style="background:#f59e0b;"></span>
                        <div>
                            <span class="gs-access-option__label">Medium</span>
                            <span class="gs-access-option__sub">Partial blocking of sensitive sections</span>
                        </div>
                    </label>
                    <label class="gs-access-option" :class="{ 'gs-access-option--active': protectionStatus == 1 }">
                        <input type="radio" v-model="protectionStatus" :value="1" @change="updateAllowFullAccess()" />
                        <span class="gs-access-option__dot" style="background:#dc2626;"></span>
                        <div>
                            <span class="gs-access-option__label">Strict</span>
                            <span class="gs-access-option__sub">Entire settings app fully blocked</span>
                        </div>
                    </label>
                    <label class="gs-access-option" :class="{ 'gs-access-option--active': protectionStatus == 2 }">
                        <input type="radio" v-model="protectionStatus" :value="2" @change="updateAllowFullAccess()" />
                        <span class="gs-access-option__dot" style="background:#16a34a;"></span>
                        <div>
                            <span class="gs-access-option__label">Relaxed</span>
                            <span class="gs-access-option__sub">Full access, only critical sections restricted</span>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Reset Password -->
            <div class="gs-card">
                <div class="gs-card__header" style="margin-bottom: 12px;">
                    <div class="gs-card__icon gs-card__icon--red">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">Reset Device Password</span>
                        <span class="gs-card__desc">Leave blank to remove the device password</span>
                    </div>
                </div>
                <div class="gs-password-row">
                    <input
                        type="password"
                        class="gs-password-input"
                        v-model="newPassword"
                        placeholder="Enter new password"
                        maxlength="50"
                    />
                    <button class="gs-password-btn" @click="issueCommand('RESET_PASSWORD')" :disabled="isDisabled">
                        <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                        <span v-else>Update</span>
                    </button>
                </div>
            </div>

            <!-- Section: Device Actions -->
            <div class="gs-section-label">Device Actions</div>

            <!-- Ring device -->
            <div class="gs-card">
                <div class="gs-card__header">
                    <div class="gs-card__icon" :class="isPlaying ? 'gs-card__icon--red' : 'gs-card__icon--blue'">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">{{ isPlaying ? 'Stop Sound' : 'Ring This Device' }}</span>
                        <span class="gs-card__desc">{{ isPlaying ? 'Stop the ringing sound on the device' : 'Play a sound to help locate the device' }}</span>
                    </div>
                    <button
                        class="gs-ring-btn"
                        :class="isPlaying ? 'gs-ring-btn--stop' : 'gs-ring-btn--play'"
                        :disabled="isDisabled"
                        @click="toggleSound()"
                    >
                        <i :class="isPlaying ? 'fas fa-stop' : 'fas fa-play'"></i>
                    </button>
                </div>
            </div>

            <!-- Section: Reports -->
            <div class="gs-section-label">Reports</div>

            <!-- Report Frequency -->
            <div class="gs-card">
                <div class="gs-card__header" style="margin-bottom: 12px;">
                    <div class="gs-card__icon gs-card__icon--purple">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="gs-card__info">
                        <span class="gs-card__title">Kids Report Frequency</span>
                        <span class="gs-card__desc">How often activity reports are emailed to you</span>
                    </div>
                </div>
                <div class="gs-report-options">
                    <label class="gs-report-option" :class="{ 'gs-report-option--active': reportFrequency === 'Off' }">
                        <input type="radio" v-model="reportFrequency" value="Off" />
                        Off
                    </label>
                    <label class="gs-report-option" :class="{ 'gs-report-option--active': reportFrequency === 'Weekly' }">
                        <input type="radio" v-model="reportFrequency" value="Weekly" />
                        Weekly
                    </label>
                    <label class="gs-report-option" :class="{ 'gs-report-option--active': reportFrequency === 'Monthly' }">
                        <input type="radio" v-model="reportFrequency" value="Monthly" />
                        Monthly
                    </label>
                </div>
            </div>

            <div class="last-updated" style="padding: 16px 0 24px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                Tap any option to save automatically
            </div>

            <!-- Reset Confirmation Modal -->
            <div class="gs-modal-overlay" v-if="showResetModal" @click.self="showResetModal = false">
                <div class="gs-modal">
                    <div class="gs-modal__header">
                        <span class="gs-modal__title">Reset All Settings?</span>
                    </div>
                    <div class="gs-modal__body">
                        All settings will be restored to their default values. This cannot be undone.
                    </div>
                    <div class="gs-modal__footer">
                        <button class="gs-modal__cancel" @click="showResetModal = false">Cancel</button>
                        <button class="gs-modal__confirm" @click="resetGlobalSetting()" :disabled="isDisabled">
                            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>Reset</span>
                        </button>
                    </div>
                </div>
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";
import ProfileService from "../../services/ProfileService";
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon.vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'

export default {
    name: 'GlobalSettings',
    components: { KidHeader, KidFooter, ArrowLeftIcon, ArrowRightIcon },
    computed: {
        displayLimit() {
            if (!this.globalDailyLimit || this.globalDailyLimit === 0) return "No Limit";
            return this.globalDailyLimit;
        },
        displayLocationLimit() {
            if (!this.locationTrackingInterval || this.locationTrackingInterval === 0) return "No Limit";
            return this.locationTrackingInterval;
        },
    },
    data() {
        return {
            profileId: this.$route.query.profileId,
            serialNumber: this.$route.query.serialNumber,
            isDisabled: false,
            globalDailyLimit: 0,
            isPlaying: false,
            reportFrequency: 'Weekly',
            newPassword: "",
            allowFullAccess: 0,
            locationTrackingInterval: 0,
            deviceId: null,
            turnOnWebMonitoring: false,
            enableNotificationSound: false,
            protectionStatus: 0,
            enableLocationTracking: true,
            limitChildApp: false,
            modify_accounts_disabled: true,
            tethering_config_disabled: true,
            showResetModal: false,
            enableSpeedAlert: false,
            speedThreshold: 60,
        }
    },
    methods: {

        updateSpeedAlert() {
            KidsService.updateDevice({
                _id: this.deviceId,
                enableSpeedAlert: this.enableSpeedAlert,
                speedAlertThreshold: this.speedThreshold,
            })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        decreaseSpeedThreshold() {
            if (this.speedThreshold > 10) {
                this.speedThreshold -= 10;
                this.updateSpeedAlert();
            }
        },
        increaseSpeedThreshold() {
            this.speedThreshold += 10;
            this.updateSpeedAlert();
        },
        openConfirmationModal() { this.showResetModal = true; },

        async issueCommand(command) {
            this.isDisabled = true;
            ProfileService.issueCommand({ type: command, newPassword: this.newPassword, serialNumber: this.serialNumber })
            .then((response) => { this.isDisabled = false; this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.isDisabled = false; this.$toast.error(e.response.data.respones_msg); });
        },
        async updateGlobalSetting(key) {
            if (this.globalDailyLimit % 15 != 0) { this.globalDailyLimit = 0; return this.$toast.error("Please enter time multiple of 15."); }
            this.isDisabled = true;
            await KidsService.updateGlobalSetting({ key, value: this.globalDailyLimit, serialNumber: this.serialNumber, profileId: this.profileId })
            .then((response) => { this.isDisabled = false; this.applyPolicy(); this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.isDisabled = false; this.$toast.error(e.response.data.respones_msg); });
        },
        async updateKidsReportFrequency() {
            await KidsService.updateKidsReportFrequency({ kidsReportType: this.reportFrequency })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        resetGlobalSetting() {
            this.isDisabled = true;
            KidsService.resetGlobalSetting({ profileId: this.profileId, serialNumber: this.serialNumber })
            .then((response) => { this.showResetModal = false; this.isDisabled = false; this.getGlobalSetting(); this.getLocationSetting(); this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.isDisabled = false; this.$toast.error(e.response.data.respones_msg); });
        },
        async applyPolicy() {
            await ProfileService.applyPolicy({ profile_id: this.profileId, serialNumber: this.serialNumber }).catch(() => {});
        },
        async getGlobalSetting() {
            await KidsService.getGlobalSetting({ key: "GlobalDailyLimit", serialNumber: this.serialNumber, profileId: this.profileId })
            .then((response) => {
                if (response.data.data) this.globalDailyLimit = parseInt(response.data.data.value);
                this.modify_accounts_disabled = response.data?.profileDetail?.modify_accounts_disabled ?? true;
                this.tethering_config_disabled = response.data?.profileDetail?.tethering_config_disabled ?? true;
                this.limitChildApp = response.data?.deviceData?.limitChildApp ?? false;
                const pe = response.data?.deviceData?.protectionEnabled;
                this.protectionStatus = pe == true ? 1 : pe == false ? 0 : pe;
                this.turnOnWebMonitoring = response.data?.deviceData?.turnOnWebMonitoring ?? false;
                this.enableNotificationSound = response.data?.deviceData?.enableNotificationSound ?? false;
                this.reportFrequency = response.data.kidsReportType;
                this.enableSpeedAlert = response.data?.deviceData?.enableSpeedAlert ?? false;
                this.speedThreshold = response.data?.deviceData?.speedAlertThreshold ?? 60;
            })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        roundOF15() { if (this.globalDailyLimit > 0) this.globalDailyLimit = Math.ceil(this.globalDailyLimit / 15.0) * 15; },
        decreaseLimit() { if (this.globalDailyLimit > 0) this.globalDailyLimit -= 15; this.updateGlobalSetting('GlobalDailyLimit'); },
        increaseLimit() { if (!this.globalDailyLimit) this.globalDailyLimit = 0; this.globalDailyLimit = parseInt(this.globalDailyLimit) + 15; this.updateGlobalSetting('GlobalDailyLimit'); },
        handleLimitInput(event) {
            const val = event.target.value.trim();
            this.globalDailyLimit = (val === "" || val.toLowerCase() === "no limit") ? 0 : (parseInt(val) || 0);
            this.updateGlobalSetting('GlobalDailyLimit');
        },
        roundOF15Location() { this.locationTrackingInterval = this.locationTrackingInterval > 0 ? Math.ceil(this.locationTrackingInterval / 5) * 5 : 5; },
        decreaseLocationLimit() { this.locationTrackingInterval = this.locationTrackingInterval > 5 ? this.locationTrackingInterval - 5 : 5; this.updateLocationSetting(); },
        increaseLocationLimit() { this.locationTrackingInterval = (!this.locationTrackingInterval || this.locationTrackingInterval < 5) ? 5 : this.locationTrackingInterval + 5; this.updateLocationSetting(); },
        handleLocationInput(event) {
            const num = parseInt(event.target.value.trim());
            this.locationTrackingInterval = (!isNaN(num) && num > 0) ? Math.ceil(num / 5) * 5 : 5;
            this.updateLocationSetting();
        },
        async getLocationSetting() {
            await KidsService.getLocationSetting({ profile_id: this.profileId, serialNumber: this.serialNumber })
            .then((response) => { this.locationTrackingInterval = response.data.data?.locationTrackingInterval ?? 15; this.deviceId = response.data.data?._id; })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        updateLocationSetting() {
            KidsService.updateDevice({ _id: this.deviceId, enableLocationTracking: this.locationTrackingInterval > 0, locationTrackingInterval: this.locationTrackingInterval })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        updateTurnOnWebMonitoring() {
            KidsService.updateDevice({ _id: this.deviceId, turnOnWebMonitoring: this.turnOnWebMonitoring })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        updateEnableNotificationSound() {
            KidsService.updateDevice({ _id: this.deviceId, enableNotificationSound: this.enableNotificationSound })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        updateLimitChildApp() {
            KidsService.updateDevice({ _id: this.deviceId, limitChildApp: this.limitChildApp })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        updateProfile() {
            KidsService.updateProfile({ profileId: this.profileId, modify_accounts_disabled: this.modify_accounts_disabled, tethering_config_disabled: this.tethering_config_disabled })
            .then((response) => { this.applyPolicy(); this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
        async sendLocationFcmCommand() {
            this.isDisabled = true;
            await KidsService.sendFcmCommand({ title: "Send Location", message: "Send kid location", packageName: "", action: "send_location", serialNumber: this.serialNumber })
            .then(() => { this.isDisabled = false; this.$toast.success("Location fetch command sent to device"); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); this.isDisabled = false; });
        },
        async toggleSound() {
            this.isDisabled = true;
            const action = this.isPlaying ? 'Stop Sound' : 'Ring a Phone';
            await KidsService.sendFcmCommand({ title: action, message: this.isPlaying ? 'Stop the ringing.' : 'Ring a phone.', action, packageName: '', serialNumber: this.serialNumber })
            .then(() => { this.isPlaying = !this.isPlaying; this.$toast.success(`${action} command sent`); })
            .catch((e) => { this.$toast.error(e.response?.data?.respones_msg || 'Failed'); })
            .finally(() => { this.isDisabled = false; });
        },
        async updateAllowFullAccess() {
            await KidsService.updateKidsProtectionStatus({ profileId: this.profileId, kidsAppProtectionStatus: this.protectionStatus, serialNumber: this.serialNumber })
            .then((response) => { this.$toast.success(response.data.respones_msg); })
            .catch((e) => { this.$toast.error(e.response.data.respones_msg); });
        },
    },
    watch: {
        reportFrequency() { this.updateKidsReportFrequency(); }
    },
    beforeMount() {
        this.getGlobalSetting();
        this.getLocationSetting();
        if (this.$route.query.isPlaying) this.toggleSound();
    },
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #374151;
    text-decoration: none; -webkit-tap-highlight-color: transparent;
}

/* ── Page header ── */
.gs-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 14px 14px; width: 100%; text-align: left;
}
.gs-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.gs-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

.gs-reset-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 12px; border-radius: 20px;
    border: 0.5px solid #e5e7eb; background: #f9fafb;
    font-size: 12px; font-weight: 600; color: #374151;
    cursor: pointer; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.gs-reset-btn:active { background: #f3f4f6; }
.gs-reset-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Section labels ── */
.gs-section-label {
    font-size: 11px; font-weight: 700; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.6px;
    padding: 14px 14px 6px;
    width: 100%;
}

/* ── Cards ── */
.gs-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    margin: 0 14px 8px; overflow: hidden; padding: 14px;
    width: 100%;
    text-align: left;
}
.gs-card--action { cursor: pointer; -webkit-tap-highlight-color: transparent; }
.gs-card--action:active { background: #f9fafb; }

.gs-card__header {
    display: flex; align-items: center; gap: 12px;
}
.gs-card__icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.gs-card__icon--blue { background: #dbeafe; color: #1d4ed8; }
.gs-card__icon--green { background: #dcfce7; color: #16a34a; }
.gs-card__icon--orange { background: #ffedd5; color: #ea580c; }
.gs-card__icon--purple { background: #ede9fe; color: #7c3aed; }
.gs-card__icon--red { background: #fee2e2; color: #dc2626; }

.gs-card__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.gs-card__title { font-size: 13px; font-weight: 600; color: #111; }
.gs-card__desc { font-size: 11px; color: #6b7280; line-height: 1.4; }
.gs-card__chevron { flex-shrink: 0; color: #9ca3af; }

/* ── Stepper ── */
.gs-stepper {
    display: flex; align-items: center;
    border: 0.5px solid #e5e7eb; border-radius: 10px;
    overflow: hidden; margin-top: 12px;
}
.gs-stepper__btn {
    width: 44px; height: 40px; background: #f9fafb; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #374151; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.gs-stepper__btn:active { background: #f3f4f6; }
.gs-stepper__btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gs-stepper__input {
    flex: 1; height: 40px; border: none;
    border-left: 0.5px solid #e5e7eb; border-right: 0.5px solid #e5e7eb;
    text-align: center; font-size: 14px; font-weight: 600;
    color: #111; background: #fff; outline: none;
}
.gs-stepper__input:disabled { background: #f9fafb; color: #9ca3af; }

/* ── Toggle rows ── */
.gs-toggle-row {
    display: flex; align-items: center; gap: 12px; padding: 4px 0;
}
.gs-toggle-row__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.gs-divider { height: 0.5px; background: #f3f4f6; margin: 12px 0; }

/* ── Switch ── */
.kid-action-switch { position: relative; display: inline-flex; width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0; }
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track { position: absolute; inset: 0; background: #d1d5db; border-radius: 999px; transition: background 0.2s; }
.kid-action-switch__track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; }
.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Access level options ── */
.gs-access-options { display: flex; flex-direction: column; gap: 8px; }
.gs-access-option {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 10px;
    border: 0.5px solid #e5e7eb; background: #f9fafb;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
    transition: all 0.15s;
}
.gs-access-option input { display: none; }
.gs-access-option--active { background: #eff6ff; border-color: #bfdbfe; }
.gs-access-option__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.gs-access-option__label { display: block; font-size: 13px; font-weight: 600; color: #111; }
.gs-access-option__sub { display: block; font-size: 11px; color: #6b7280; line-height: 1.3; }

/* ── Password row ── */
.gs-password-row { display: flex; align-items: center; gap: 8px; }
.gs-password-input {
    flex: 1; height: 42px; border: 0.5px solid #e5e7eb;
    border-radius: 10px; padding: 0 12px; font-size: 14px;
    color: #111; background: #fafafa; outline: none;
}
.gs-password-input:focus { border-color: #2563eb; background: #fff; }
.gs-password-btn {
    height: 42px; padding: 0 16px; background: #2563eb; color: #fff;
    border: none; border-radius: 10px; font-size: 13px; font-weight: 600;
    cursor: pointer; flex-shrink: 0; -webkit-tap-highlight-color: transparent;
}
.gs-password-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Ring button ── */
.gs-ring-btn {
    width: 36px; height: 36px; border-radius: 10px; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; flex-shrink: 0; font-size: 13px;
    -webkit-tap-highlight-color: transparent;
}
.gs-ring-btn--play { background: #dbeafe; color: #1d4ed8; }
.gs-ring-btn--stop { background: #fee2e2; color: #dc2626; }
.gs-ring-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Report options ── */
.gs-report-options { display: flex; gap: 8px; }
.gs-report-option {
    flex: 1; display: flex; align-items: center; justify-content: center;
    padding: 9px 0; border-radius: 10px;
    border: 0.5px solid #e5e7eb; background: #f9fafb;
    font-size: 13px; font-weight: 500; color: #374151;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
    transition: all 0.15s;
}
.gs-report-option input { display: none; }
.gs-report-option--active { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; font-weight: 600; }

/* ── Reset modal ── */
.gs-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 1050; display: flex; align-items: flex-end; justify-content: center;
}
.gs-modal {
    background: #fff; border-radius: 20px 20px 0 0;
    width: 100%; max-width: 600px; padding: 20px 16px 24px;
    animation: slideUp 0.25s ease;
}
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.gs-modal__header { margin-bottom: 10px; }
.gs-modal__title { font-size: 16px; font-weight: 700; color: #111; }
.gs-modal__body { font-size: 13px; color: #6b7280; line-height: 1.5; margin-bottom: 20px; }
.gs-modal__footer { display: flex; gap: 10px; }
.gs-modal__cancel {
    flex: 1; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
}
.gs-modal__confirm {
    flex: 1; height: 44px; border-radius: 12px; border: none;
    background: #dc2626; color: #fff;
    font-size: 14px; font-weight: 600; cursor: pointer;
}
.gs-modal__confirm:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Last updated ── */
.last-updated {
    display: flex; align-items: center; justify-content: center;
    gap: 5px; font-size: 12px; color: #9ca3af;
}

button:focus { outline: unset; }

/* ── Speed threshold ── */
.gs-speed-threshold {
    margin-top: 12px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 10px;
    border: 0.5px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.gs-speed-threshold__label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    flex: 1;
}

.gs-speed-stepper {
    display: flex;
    align-items: center;
    border: 0.5px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    flex-shrink: 0;
}

.gs-speed-stepper__val {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    min-width: 64px;
    border-left: 0.5px solid #e5e7eb;
    border-right: 0.5px solid #e5e7eb;
    height: 40px;
    gap: 1px;
}

.gs-speed-stepper__num {
    font-size: 15px;
    font-weight: 700;
    color: #111;
    line-height: 1;
}

.gs-speed-stepper__unit {
    font-size: 9px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
</style>